import { makeNonSerializableStateStore } from '@playcastdotio/StateStore';
import { NonSerializableStateStore } from '@playcastdotio/CoreTypes';
import { sendAnalytics, PlaycastAnalyticsEvent } from '@playcastdotio/Analytics';

export type FeatureStatus = 'idle' | 'initializing' | 'ready' | 'error' | 'cleanup';

export interface PlayerAccessRights {
  mouse?: boolean;
  keyboard?: boolean;
  touch?: boolean;
  gamepad?: boolean;
}

export interface BaseFeatureState {
  status: FeatureStatus;
  error?: string;
  /**
   * Input access rights for this feature.
   * Controls what input methods are enabled (mouse, keyboard, touch, gamepad).
   * Default: all disabled (host or feature must explicitly enable).
   */
  access?: PlayerAccessRights;
}

/**
 * Base Feature Controller
 *
 * Provides:
 * - Scoped state management via StateStore
 * - Automatic cleanup on destroy
 * - Analytics integration with automatic event sending
 * - Safe subscription management to global stores
 *
 * The destroyed flag is exposed as a public getter so external consumers
 * (e.g. the admin-dashboard's `useDeploymentWizard` React hook) can detect
 * a torn-down controller across React 18 Strict Mode remounts without
 * widening write access. Subclasses still read it the same way they always
 * have (`this.isDestroyed`); only the cleanup() path inside this base
 * mutates the underlying flag.
 */
export abstract class FeatureController<TState extends BaseFeatureState> {
  protected state: NonSerializableStateStore<TState>;
  // Per LOW 20 fix (deployment-audit-fixes Phase 4): tightened from
  // `protected` to `private`. Subclasses register cleanup callbacks via the
  // protected `addCleanup(fn)` helper below — they have no legitimate need
  // to mutate the underlying stack directly, and forbidding it at the type
  // level prevents future subclasses from accidentally re-ordering or
  // dropping registered cleanups.
  private cleanupFunctions: Array<() => void> = [];
  private _isDestroyed = false;
  protected analyticsEnabled = true;

  constructor(initialState: TState) {
    this.state = makeNonSerializableStateStore(initialState);
    console.log(`[${this.constructor.name}] Created`);
  }

  /**
   * Public read-only view of the destroyed flag. Used by React hooks that
   * need to detect a controller torn down in a Strict Mode setup→cleanup→
   * setup cycle and rebuild before invoking actions on the stale ref.
   */
  public get isDestroyed(): boolean {
    return this._isDestroyed;
  }

  /**
   * Get state store - React components subscribe to this
   */
  public getState(): NonSerializableStateStore<TState> {
    return this.state;
  }

  /**
   * Initialize the feature - called once when component mounts
   */
  public async init(): Promise<void> {
    if (this.isDestroyed) {
      console.warn(`[${this.constructor.name}] Cannot init - already destroyed`);
      return;
    }

    try {
      this.updateStatus('initializing');
      await this.onInit();
      this.updateStatus('ready');
    } catch (error) {
      console.error(`[${this.constructor.name}] Init error:`, error);
      this.updateStatus('error', error instanceof Error ? error.message : 'Unknown error');
    }
  }

  /**
   * Override this in subclasses for initialization logic
   */
  protected abstract onInit(): Promise<void>;

  /**
   * Cleanup - called when React component unmounts.
   *
   * NOTE: The final step (`this.state.reset(true)`) wipes the scoped store
   * back to its constructor-supplied initial value. Consumers that need to
   * inspect the final wizard / feature state for logging or analytics must
   * snapshot it *before* invoking `cleanup()`. Action methods invoked
   * after cleanup are guaranteed not to mutate the store further because
   * `isDestroyed` short-circuits them.
   */
  public cleanup(): void {
    if (this.isDestroyed) return;

    console.log(`[${this.constructor.name}] Cleanup started`);
    this._isDestroyed = true;

    this.updateStatus('cleanup');

    // Run all cleanup functions in reverse order (LIFO)
    while (this.cleanupFunctions.length > 0) {
      const cleanup = this.cleanupFunctions.pop();
      try {
        cleanup?.();
      } catch (error) {
        console.error(`[${this.constructor.name}] Cleanup error:`, error);
      }
    }

    // Reset state store
    this.state.reset(true);

    console.log(`[${this.constructor.name}] Cleanup complete`);
  }

  /**
   * Helper to register cleanup functions
   */
  protected addCleanup(fn: () => void): void {
    this.cleanupFunctions.push(fn);
  }

  /**
   * Helper to subscribe to global stores safely
   * Automatically registers cleanup
   */
  protected subscribeToGlobalStore<T>(
    store: NonSerializableStateStore<T>,
    handler: (state: T, previousState?: T) => void
  ): void {
    const listenerId = store.addListener(handler);
    this.addCleanup(() => store.removeListener(listenerId));
  }

  /**
   * Guard against accessing destroyed controller
   */
  protected assertNotDestroyed(): void {
    if (this.isDestroyed) {
      throw new Error(`[${this.constructor.name}] Cannot perform action - controller is destroyed`);
    }
  }

  /**
   * Update status field in state
   */
  protected updateStatus(status: FeatureStatus, error?: string): void {
    this.state.updateState({
      ...this.state.currentState,
      status,
      error,
    } as TState);
  }

  /**
   * Send analytics event
   * Automatically disabled when controller is destroyed
   */
  protected sendAnalytics(event: PlaycastAnalyticsEvent): void {
    if (!this.analyticsEnabled || this.isDestroyed) {
      console.log(`[${this.constructor.name}] Analytics disabled or destroyed, skipping:`, event.name);
      return;
    }

    try {
      console.log(`[${this.constructor.name}] Analytics:`, event.name, event);
      sendAnalytics(event);
    } catch (error) {
      console.error(`[${this.constructor.name}] Analytics error:`, error);
    }
  }

  /**
   * Disable analytics (useful for testing)
   */
  public disableAnalytics(): void {
    this.analyticsEnabled = false;
  }

  /**
   * Create a managed timeout that auto-cleans up
   */
  protected createTimeout(callback: () => void, delay: number): NodeJS.Timeout {
    const timeoutId = setTimeout(() => {
      if (!this.isDestroyed) {
        callback();
      }
    }, delay);

    this.addCleanup(() => clearTimeout(timeoutId));
    return timeoutId;
  }

  /**
   * Create a managed interval that auto-cleans up
   */
  protected createInterval(callback: () => void, delay: number): NodeJS.Timeout {
    const intervalId = setInterval(() => {
      if (!this.isDestroyed) {
        callback();
      }
    }, delay);

    this.addCleanup(() => clearInterval(intervalId));
    return intervalId;
  }

  /**
   * Add DOM event listener with automatic cleanup
   * Use this instead of window.addEventListener to ensure proper cleanup
   */
  protected addEventListenerSafe(
    target: EventTarget,
    event: string,
    handler: EventListener,
    options?: AddEventListenerOptions
  ): void {
    target.addEventListener(event, handler, options);
    this.addCleanup(() => target.removeEventListener(event, handler, options));
  }
}
